import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { BsArrowUpRight, BsArrowDownLeft } from "react-icons/bs";
import moment from "moment";

const CustomerGrowthChart = ({ customers = [] }) => {
  const { customerData, total, diff } = useMemo(() => {
    const list = Array.isArray(customers)
      ? customers
      : Array.isArray(customers?.users)
        ? customers.users
        : [];

    const grouped = {};

    list.forEach((customer) => {
      if (!customer?.createdAt) return;

      const createdAt = moment(customer.createdAt);
      if (!createdAt.isValid()) return;

      const date = createdAt.format("YYYY-MM-DD");
      grouped[date] = (grouped[date] || 0) + 1;
    });

    const customerData = Object.entries(grouped)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Compare today with yesterday, including days with zero signups.
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");

    return {
      customerData,
      total: list.length,
      diff: (grouped[today] || 0) - (grouped[yesterday] || 0),
    };
  }, [customers]);

  const growth = diff > 0;

  return (
    <div className="bg-drylight dark:bg-neutral-800 rounded-xl border border-border p-5 shadow w-full">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <h2 className="text-base font-semibold text-yellowdark">
          Customer Growth
        </h2>

        <div className="flex flex-wrap items-center gap-2 text-sm text-textGray">
          <span className="font-semibold text-lg text-text">{total}</span>
          Customers loaded
          {diff !== 0 && (
            <span
              className={`flex items-center ml-2 font-medium ${
                growth ? "text-green-500" : "text-red-500"
              }`}
            >
              {growth ? <BsArrowUpRight /> : <BsArrowDownLeft />}

              <span className="ml-1">
                {growth ? "+" : ""}
                {diff} vs yesterday
              </span>
            </span>
          )}
        </div>
      </div>

      <p className="text-xs text-textGray">Based on loaded customers only.</p>

      <div className="h-64 w-full mt-4">
        {customerData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-textGray">
            No customer registration dates available.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={customerData}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(date) => moment(date).format("MMM D")}
                stroke="#fed693"
              />

              <YAxis stroke="#fed693" allowDecimals={false} />

              <Tooltip
                labelFormatter={(label) => moment(label).format("MMMM D, YYYY")}
              />

              <Line
                type="monotone"
                dataKey="count"
                name="New customers"
                stroke="#fed693"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CustomerGrowthChart;
